"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { useDebounce } from "@/hooks/useDebounce"
import { asArray } from "@/lib/as-array"
import {
  mapApiCandidateToResumeForm,
  mapResumeFormToApiCandidate,
  type ResumeFormData,
} from "@/components/reusable/add-candidates-dialog"
import { splitCandidateName } from "@/utils/candidate-name"
import {
  useGetCandidates,
  useGetJobsJobIdPipeline,
  usePostCandidates,
  usePostCandidatesParseResume,
  usePostContactsCandidate,
  usePostFilesOwnerOwnerId,
  usePostJobsJobIdPipelineAdd,
  usePostJobsJobIdPipelineResumeAdd,
  usePutJobsJobIdPipelineStage,
} from "openapi/api/endpoints"

/**
 * Putting a person on a job, from canvas 19a–19d.
 *
 * "Four sources become four tabs in one sheet… the stage is stated once, above
 * the tabs, and governs whichever source you use." So the stage lives here, not
 * inside each source, and every write goes through the same pipeline add.
 */

export type AddSource = "pool" | "linkedin" | "resume" | "manual"

/**
 * Where `POST /jobs/{id}/pipeline/resume-add` puts someone. It takes no stage,
 * so a sheet asking for anything else has to move them afterwards.
 */
const RESUME_ADD_LANDS_IN = "longlist"

const RESUME_TYPES = [".pdf", ".doc", ".docx"]

export const emptyCandidateDraft = (): ResumeFormData => ({
  full_name: "",
  email: "",
  headline: "",
  summary: "",
  skills: [],
  location: "",
  current_role_title: "",
  source: "resume_upload",
  linkedin_profile_url: "",
  phone_number: "",
  experiences: [],
})

export type PoolRow = {
  id: string
  name: string
  initials: string
  role: string
  /** Where the record came from — linkedin, referral, resume. */
  source?: string
  /** Years of experience and where they are, as one mono line. */
  standing: string
  /** Already on this job: 19a keeps these visible, dimmed and ticked. */
  assignedStage?: string
}

function initialsOf(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

/** Whole years across a candidate's roles, merging overlaps. */
function yearsOf(experiences?: { starts_at?: string | null; ends_at?: string | null }[]) {
  const ranges: [number, number][] = []
  for (const experience of experiences ?? []) {
    if (!experience?.starts_at) continue
    const start = new Date(experience.starts_at).getTime()
    const end = experience.ends_at ? new Date(experience.ends_at).getTime() : Date.now()
    if (Number.isNaN(start) || Number.isNaN(end) || end < start) continue
    ranges.push([start, end])
  }
  if (ranges.length === 0) return undefined

  ranges.sort((a, b) => a[0] - b[0])
  let [, covered] = [0, 0]
  let total = 0
  let cursor = -Infinity
  for (const [start, end] of ranges) {
    const from = Math.max(start, cursor)
    if (end > from) total += end - from
    cursor = Math.max(cursor, end)
  }
  covered = total
  const years = Math.floor(covered / (365.25 * 24 * 60 * 60 * 1000))
  return years < 1 ? "<1y" : `${years}y`
}

export function useAddCandidate({
  jobId,
  isOpen,
  onClose,
  initialSource = "pool",
}: {
  jobId: string
  isOpen: boolean
  onClose: () => void
  initialSource?: AddSource
}) {
  const queryClient = useQueryClient()

  const [source, setSource] = useState<AddSource>(initialSource)
  const [stage, setStage] = useState("longlist")
  const [query, setQuery] = useState("")
  const [picked, setPicked] = useState<Set<string>>(new Set())
  const [linkedinUrl, setLinkedinUrl] = useState("")
  const [resumeFile, setResumeFile] = useState<File | null>(null)
  const [parsed, setParsed] = useState<ResumeFormData | null>(null)
  const [manual, setManual] = useState<ResumeFormData>(emptyCandidateDraft)

  const debounced = useDebounce(query, 400)
  const search = debounced.trim()

  /*
    Reset when the sheet opens, not when it closes: the tab it should open on
    is only known at that moment. 18c's two routes both open this sheet, and
    the one that creates someone asks for a different source than the one that
    searches the pool — resetting on close would have applied the previous
    request's source and left the new one ignored.
  */
  const wasOpen = useRef(isOpen)
  useEffect(() => {
    if (isOpen && !wasOpen.current) {
      setQuery("")
      setPicked(new Set())
      setLinkedinUrl("")
      setResumeFile(null)
      setParsed(null)
      setManual(emptyCandidateDraft())
      setSource(initialSource)
    }
    wasOpen.current = isOpen
  }, [isOpen, initialSource])

  const { data: pipeline } = useGetJobsJobIdPipeline(jobId, {
    query: { enabled: Boolean(jobId) && isOpen },
  })

  /*
    19a keeps candidates already on the job in the list rather than filtering
    them out — "seeing that Maya is already here is the answer to a question you
    were about to ask" — so this maps id to the stage they are in.
  */
  const assigned = useMemo(() => {
    const held = new Map<string, string>()
    for (const [stageId, column] of Object.entries(pipeline ?? {})) {
      const ids = (column as { candidateIds?: string[] })?.candidateIds ?? []
      for (const id of ids) held.set(String(id), stageId)
    }
    return held
  }, [pipeline])

  const { data: candidatesResponse, isFetching } = useGetCandidates(
    { search_str: search || undefined },
    { query: { enabled: isOpen && source === "pool" && search.length > 0 } },
  )

  const rows = useMemo<PoolRow[]>(() => {
    const candidates = asArray<Record<string, unknown>>(
      (candidatesResponse as { candidates?: unknown })?.candidates,
    )
    return candidates
      .filter((candidate) => Boolean(candidate.candidate_id))
      .map((candidate) => {
        const id = String(candidate.candidate_id)
        const name = (candidate.full_name as string) || "Unnamed candidate"
        const company = (candidate.experiences as { company?: string }[] | undefined)?.[0]
          ?.company
        const role =
          [(candidate.current_role_title as string) ?? (candidate.headline as string), company]
            .filter(Boolean)
            .join(" · ") || "—"

        const years = yearsOf(
          candidate.experiences as { starts_at?: string | null; ends_at?: string | null }[],
        )
        const where = (candidate.location as string) ?? undefined

        return {
          id,
          name,
          initials: initialsOf(name),
          role,
          source: (candidate.source as string) ?? undefined,
          standing: [years, where].filter(Boolean).join(" · ") || "—",
          assignedStage: assigned.get(id),
        }
      })
  }, [assigned, candidatesResponse])

  const alreadyHere = rows.filter((row) => row.assignedStage).length

  const toggle = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const { mutateAsync: addToPipeline, isPending: isAdding } = usePostJobsJobIdPipelineAdd()
  const { mutateAsync: createFromLinkedin, isPending: isCreating } = usePostCandidates()
  const { mutateAsync: parseResume, isPending: isParsing } = usePostCandidatesParseResume()
  const { mutateAsync: createOnJob, isPending: isCreatingOnJob } =
    usePostJobsJobIdPipelineResumeAdd()
  const { mutateAsync: uploadFile } = usePostFilesOwnerOwnerId()
  const { mutateAsync: createContact } = usePostContactsCandidate()
  const { mutateAsync: moveStage } = usePutJobsJobIdPipelineStage()

  const refresh = async () => {
    await queryClient.invalidateQueries({
      queryKey: [`/jobs/${jobId}/pipeline`],
      exact: false,
    })
  }

  /*
    `resume-add` creates the candidate and puts them on the job in one call, but
    the call carries no stage. 19a promises the stage line governs every source,
    so anything other than where that endpoint lands is a move afterwards.
  */
  const placeAtChosenStage = async (candidateId: string) => {
    if (stage === RESUME_ADD_LANDS_IN) return
    await moveStage({ jobId, data: { candidate_id: candidateId, stage } })
  }

  /** Reads a résumé without saving anything, so it can be shown first. */
  const pickResume = async (file: File) => {
    const named = file.name.toLowerCase()
    if (!RESUME_TYPES.some((type) => named.endsWith(type))) {
      toast.error("That file cannot be read", {
        description: "Résumés can be PDF, DOC or DOCX.",
      })
      return
    }

    setResumeFile(file)
    setParsed(null)
    try {
      setParsed(mapApiCandidateToResumeForm(await parseResume({ data: { resume: file } })))
    } catch (error) {
      setResumeFile(null)
      toast.error("Could not read that résumé", { description: `${error}` })
    }
  }

  const clearResume = () => {
    setResumeFile(null)
    setParsed(null)
  }

  /** The contact record both write paths create alongside the candidate. */
  const recordContact = async (candidateId: string, draft: ResumeFormData) => {
    const { firstName, lastName } = splitCandidateName(draft.full_name)
    await createContact({
      data: {
        contact_type: "candidate",
        first_name: firstName,
        last_name: lastName || undefined,
        email: draft.email,
        phone: draft.phone_number ?? "",
        candidate_id: candidateId,
        // The contact source enum only knows "resume"; both paths are typed in.
        source: "resume",
      },
    })
  }

  const addFromDraft = async (draft: ResumeFormData, file?: File | null) => {
    if (!draft.full_name.trim() || !draft.email.trim()) {
      toast.error("A name and an email are needed", {
        description: "Both are required to create the candidate.",
      })
      return
    }

    try {
      const candidate = await createOnJob({
        jobId,
        data: mapResumeFormToApiCandidate(draft),
      })
      const candidateId = candidate.candidate_id as string
      if (!candidateId) throw new Error("The candidate came back without an id.")

      if (file) {
        await uploadFile({
          owner: "candidate",
          ownerId: candidateId,
          data: { file, name: "resume" },
        })
        await queryClient.invalidateQueries({ queryKey: [`/files/${candidateId}`] })
      }

      await recordContact(candidateId, draft)
      await placeAtChosenStage(candidateId)
      await refresh()

      toast.success(`${draft.full_name} added to ${stage}`)
      onClose()
    } catch (error) {
      toast.error("Could not add the candidate", { description: `${error}` })
    }
  }

  const addPicked = async () => {
    const candidateIds = [...picked]
    if (candidateIds.length === 0) return

    try {
      await addToPipeline({ jobId, data: { candidateIds, stage } })
      await refresh()
      toast.success(
        `${candidateIds.length} added to ${stage}`,
        candidateIds.length === 1 ? undefined : { description: "They are on the pipeline now." },
      )
      onClose()
    } catch (error) {
      toast.error("Could not add to the pipeline", { description: `${error}` })
    }
  }

  /*
    The shipped LinkedIn path posted the URL and dropped whatever came back into
    `longlist` with no choice. The stage is now the sheet's, but the candidate is
    still created before anything can be shown: there is no parse-only endpoint
    for a profile, so 19c's "read, not saved yet" preview has nothing behind it.
  */
  const addLinkedin = async () => {
    const url = linkedinUrl.trim()
    if (!url) return

    try {
      const created = await createFromLinkedin({ data: { linkedin_url: url } })
      const candidateId = (created as { candidate?: { id?: string } })?.candidate?.id
      if (!candidateId) throw new Error("The profile did not come back with a candidate.")

      await addToPipeline({ jobId, data: { candidateIds: [candidateId], stage } })
      await refresh()
      toast.success(`Added to ${stage}`)
      onClose()
    } catch (error) {
      toast.error("Could not add from LinkedIn", { description: `${error}` })
    }
  }

  return {
    source,
    setSource,
    stage,
    setStage,

    query,
    setQuery,
    rows,
    isSearching: isFetching,
    hasQuery: search.length > 0,
    alreadyHere,

    picked,
    pickedCount: picked.size,
    toggle,

    linkedinUrl,
    setLinkedinUrl,

    resumeFile,
    parsed,
    isParsing,
    pickResume,
    clearResume,
    setParsedField: (field: keyof ResumeFormData, value: string) =>
      setParsed((prev) => (prev ? { ...prev, [field]: value } : prev)),

    manual,
    setManualField: (field: keyof ResumeFormData, value: string) =>
      setManual((prev) => ({ ...prev, [field]: value })),

    isSaving: isAdding || isCreating || isCreatingOnJob,
    addPicked,
    addLinkedin,
    addResume: () => addFromDraft(parsed ?? emptyCandidateDraft(), resumeFile),
    addManual: () => addFromDraft(manual),
  }
}
